// src/modules/customers/api.js
import API from '../../services/api';
import { headerSafeFileName } from '../../utils/headerSafeFileName';

// Fetch all customers
// export const fetchCustomers = (search = '') =>
//     API.get(`/customers${search ? `?search=${encodeURIComponent(search)}` : ''}`);

// --- DigiLocker (event-driven: webhook + redirect callback) ---
// Every call here reads/writes OUR session row. Only startDigilocker and a
// server-throttled refreshDigilocker ever reach Sandbox's billed API.
export const startDigilocker = async (data = {}) => {
  const response = await API.post('/customers/kyc/digilocker/sessions', data);
  return response.data;
};

export const getDigilockerSession = async (ref) => {
  const response = await API.get(`/customers/kyc/digilocker/sessions/${ref}`);
  return response.data;
};

export const refreshDigilocker = async (ref) => {
  const response = await API.post(`/customers/kyc/digilocker/sessions/${ref}/refresh`);
  return response.data;
};

export const retryDigilockerFetch = async (ref) => {
  const response = await API.post(`/customers/kyc/digilocker/sessions/${ref}/retry`);
  return response.data;
};

// --- PAN verification ---
export const verifyPan = async (data) => {
  const response = await API.post('/customers/kyc/pan/verify', data);
  return response.data;
};

// deleted: true (admin only) lists the soft-deleted customers instead.
export const fetchCustomers = ({ search = '', page = 1, limit = 10, deleted = false }) =>
  API.get('/customers', {
    params: { search, page, limit, ...(deleted ? { deleted: true } : {}) },
  }).then((res) => res.data);

// Get a single customer by ID
export const getCustomer = (id) => API.get(`/customers/${id}`);

// Create a new customer
export const createCustomer = (data) => API.post('/customers', data);

// Update an existing customer
export const updateCustomer = (id, data) => API.put(`/customers/${id}`, data);

// Update customer photo
export const updateCustomerPhoto = async (customerId, photoData) => {
  try {
    const response = await API.put(`/customers/${customerId}/photo`, { photo: photoData });
    return response.data;
  } catch (error) {
    console.error('Error updating customer photo:', error);
    throw error;
  }
};

// Upload raw image
export const uploadRawImage = async (file, { fileName, folderId } = {}) => {
  console.log('[Frontend] uploadRawImage called with file:', file?.name || 'unnamed', 'size:', file?.size);
  if (!file) {
    throw new Error('File is required for upload');
  }

  const resolvedName = fileName || file.name || `auriksha_upload_${Date.now()}.jpg`;
  const headers = {
    'Content-Type': 'application/octet-stream',
    'x-file-name': headerSafeFileName(resolvedName)
  };

  if (folderId) {
    headers['x-folder-path'] = folderId; // Updated to match ImageKit service
  }

  console.log('[Frontend] Uploading to /uploads/buffer with headers:', headers);
  try {
    const response = await API.post('/uploads/buffer', file, { // Updated to new route
      headers,
      transformRequest: [(data) => data],
      maxBodyLength: Infinity
    });
    console.log('[Frontend] Upload successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('[Frontend] Upload failed:', error);
    throw error;
  }
};

// Delete a customer
// Soft delete by default; { permanent: true } is an admin-only hard delete.
export const deleteCustomer = (id, { permanent = false } = {}) =>
  API.delete(`/customers/${id}`, { params: permanent ? { permanent: true } : {} });

export const restoreCustomer = (id) => API.post(`/customers/${id}/restore`);

// ── Approval-gated editing ──
// Current edit request, edit history and the list of fields that can be requested.
export const getCustomerEditState = (id) =>
  API.get(`/customers/${id}/edit-request`).then((res) => res.data);

export const requestCustomerEdit = (id, { fields, reason }) =>
  API.post(`/customers/${id}/edit-request`, { fields, reason }).then((res) => res.data);

export const cancelCustomerEdit = (id) =>
  API.delete(`/customers/${id}/edit-request`).then((res) => res.data);


export const getCustomerById = (id) => API.get(`/customers/${id}`);

// ── Saved bank accounts ──
// First save is free; later changes need an approved 'bankAccounts' edit (admins direct).
export const saveCustomerBankAccounts = (id, accounts) =>
  API.put(`/customers/${id}/bank-accounts`, { accounts }).then((res) => res.data);

// Bank + branch for an IFSC code.
export const lookupIfsc = (code) =>
  API.get(`/customers/ifsc/${encodeURIComponent(code)}`).then((res) => res.data?.data);

// ── Uploaded documents (reference only, append-only; admin removes) ──
export const addCustomerDocument = (id, { documentType, label, file }) =>
  API.post(`/customers/${id}/documents`, { documentType, label, file }).then((res) => res.data);

export const removeCustomerDocument = (id, docId) =>
  API.delete(`/customers/${id}/documents/${docId}`).then((res) => res.data);
